import cx from 'classnames';
import type { FunctionComponent, InputHTMLAttributes } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { TaskList } from './TaskList.js';
import { useCloudpack } from '../CloudpackProvider/CloudpackProvider.js';
import { Button } from '../Button/Button.js';
import { Dialog } from '../Dialog/Dialog.js';
import { elementIds } from '../../constants.js';
import styles from './StatusDialog.module.css';
import inputStyles from '../Button/inputCommon.module.css';
import { Refresh20 } from '../icons/Refresh20.js';

export interface StatusDialogProps {
  onClose: () => void;
}

export const StatusDialog: FunctionComponent<StatusDialogProps> = ({ onClose }) => {
  const cloudpack = useCloudpack();
  const [searchFilter, setSearchFilter] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const handleSearchChange = useCallback<NonNullable<InputHTMLAttributes['onInput']>>((event) => {
    event.preventDefault();
    setSearchFilter((event.target as HTMLInputElement).value);
  }, []);

  const restartAllTasks = () => {
    setIsRestarting(true);
    void cloudpack.restartAllTasks.mutate();
  };

  const toggleIssuesOnly = () => void setIssuesOnly((value) => !value);

  return (
    <Dialog
      title="Cloudpack bundle results"
      id={elementIds.statusDialogRoot}
      draggable
      // Prevent closing the dialog and disable all the controls while restarting, to hopefully
      // avoid confusion (restarting currently involves clearing the whole cache and may take awhile).
      onClose={isRestarting ? undefined : onClose}
      commands={
        <>
          {/* Disable the button after it's clicked since the operation may take awhile, and the
              page will refresh when finished.
              TODO: ideally we'd have a spinner while restarting (this could probably be done by
              animating the refresh icon) */}
          <Button variant="primary" onClick={restartAllTasks} disabled={isRestarting}>
            {/* the refresh icon spins while restarting to show something is happening */}
            <Refresh20 class={cx(isRestarting && styles.spin)} /> Re-bundle all packages
          </Button>
          <Button onClick={toggleIssuesOnly} disabled={isRestarting}>
            {issuesOnly ? 'Show all packages' : 'Show packages with issues'}
          </Button>
          <input
            type="text"
            aria-label="Search"
            placeholder="Search"
            value={searchFilter}
            onInput={handleSearchChange}
            class={cx(styles.searchbox, inputStyles.root, inputStyles.standard)}
            disabled={isRestarting}
          />
        </>
      }
    >
      {isRestarting ? (
        'Preparing to re-bundle (the page will refresh when ready)...'
      ) : (
        <TaskList searchFilter={searchFilter} issuesOnly={issuesOnly} />
      )}
    </Dialog>
  );
};

export default StatusDialog;
